'use client'

import { useEffect, useId } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import type { AnnounceOptions } from './state'
import { useAnnounce } from './useAnnounce'

// Long enough to collapse a filter typed per keystroke into one announcement, short enough
// that a result count still feels attached to the action that produced it.
export const ANNOUNCE_DEBOUNCE_MS = 300

// Announces `text` whenever it changes, on mount too, so a keyed remount announces. Pass null
// (or '') to say nothing. A component remounted under a changing key is a new hook instance
// with a new id, so a caller in that position names its `source` to keep replacing the
// message the previous instance made.
export function useAnnouncement(
  text: string | null,
  { assertive = false, source: namedSource }: AnnounceOptions = {}
) {
  const { announce } = useAnnounce()
  const instanceId = useId()
  const source = namedSource ?? instanceId
  const announceDebounced = useDebouncedCallback(announce, ANNOUNCE_DEBOUNCE_MS)

  useEffect(() => {
    // A message still in the debounce window describes a state that is gone; drop it rather
    // than speak "ready" after the dialog closed.
    if (!text) {
      announceDebounced.cancel()
      return
    }
    announceDebounced(text, { assertive, source })
  }, [text, assertive, source, announceDebounced])

  useEffect(() => announceDebounced.cancel, [announceDebounced])
}
